import {
  IconSettings,
  IconLogout,
  IconLayoutDashboard,
  IconClipboardText,
  IconChartBar,
  IconReceipt2,
  IconKey,
  IconChevronUp,
  IconUser,
  IconChevronDown,
  IconUsers,
  IconHeadset,
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { AppShell, Burger } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearLocalStorage } from '../utils/authUtils';
import { AdminOnlyComponent } from './ProtectedRoute';

interface BodyWrapProps {
  children: React.ReactNode;
}

const BodyWrap: React.FC<BodyWrapProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [opened, { toggle }] = useDisclosure();

  function handleLogout() {
    clearLocalStorage();
    navigate('/login');
  }

  const NavItem = ({
    label,
    icon,
    path,
  }: {
    label: string;
    icon: React.ReactNode;
    path: string;
  }) => (
    <div
      className={`w-full h-11 flex items-center pl-5 hover:bg-[#fafafa] cursor-pointer ${
        isActive(path) ? 'bg-[#fafafa]' : ''
      }`}
      onClick={() => navigate(path)}
    >
      {icon}
      <span className="ml-2">{label}</span>
    </div>
  );

  const SubNavItem = ({
    label,
    icon,
    path,
  }: {
    label: string;
    icon: React.ReactNode;
    path: string;
  }) => (
    <div
      className={`w-full h-10 flex items-center pl-5 hover:bg-[#f0f0f0] cursor-pointer ${
        isActive(path) ? 'bg-[#f0f0f0]' : ''
      }`}
      onClick={() => navigate(path)}
    >
      {icon}
      <span className="ml-2">{label}</span>
    </div>
  );

  // Helper function to check if the current route matches the target route
  const isActive = (path: string) => {
    const firstSubRoute = `/${location.pathname.split('/')[1]}`; // Get the first segment of the route
    return firstSubRoute === path;
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 300,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
      className="bg-[#f9f9f9]"
    >
      <AppShell.Header className="bg-[#f8f7f2]">
        <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
        <NavLink to="/dashboard">
          <img
            src="/logo_black.png"
            alt="PromptSlate logo"
            className="h-[30px] object-contain self-center rounded-md mt-4 ml-5"
          />
        </NavLink>
      </AppShell.Header>
      <AppShell.Navbar p="md">
        <div>
          <div className="w-full flex flex-col mt-6 flex-grow">
            {/* Regular nav items */}
            <NavItem
              label="Dashboard"
              icon={<IconLayoutDashboard size="1rem" />}
              path="/dashboard"
            />
            <NavItem label="Analytics" icon={<IconChartBar size="1rem" />} path="/analytics" />
            <NavItem label="API Keys" icon={<IconKey size="1rem" />} path="/api" />
            <NavItem label="API Docs" icon={<IconClipboardText size="1rem" />} path="/api-docs" />
            {/* Settings dropdown */}
            <div
              className={`w-full h-11 flex items-center pl-5 hover:bg-[#fafafa] cursor-pointer ${
                isActive('/settings') ? 'bg-[#fafafa]' : ''
              }`}
              onClick={() => setSettingsOpen((prev) => !prev)}
            >
              <IconSettings size="1rem" className="mr-2" /> Settings
              <span className="ml-auto pr-4">
                {settingsOpen ? <IconChevronUp size="1rem" /> : <IconChevronDown size="1rem" />}
              </span>
            </div>
            {settingsOpen && (
              <div className="ml-6 flex flex-col">
                <AdminOnlyComponent>
                  <SubNavItem
                    label="Users"
                    icon={<IconUsers size="1rem" />}
                    path="/settings/users"
                  />
                </AdminOnlyComponent>
                <AdminOnlyComponent>
                  <SubNavItem
                    label="Billing"
                    icon={<IconReceipt2 size="1rem" />}
                    path="/settings/billing"
                  />
                </AdminOnlyComponent>
                <SubNavItem
                  label="Account & Security"
                  icon={<IconUser size="1rem" />}
                  path="/settings/account"
                />
                <SubNavItem
                  label="Customer support"
                  icon={<IconHeadset size="1rem" />}
                  path="mailto:support@promptslate.com"
                />
              </div>
            )}
          </div>

          {/* Logout */}
          <div
            className="w-full h-11 flex items-center pl-5 hover:bg-[#fafafa] mb-5 cursor-pointer"
            onClick={handleLogout}
          >
            <IconLogout size="1rem" className="mr-2" /> Logout
          </div>
        </div>
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
};

export default BodyWrap;
